import * as React from "react";

import Typography from "@material-ui/core/Typography";

// jaza-soft
import { translate } from "jazasoft";

import MuiTable from "../../../../components/MuiTable";

import { isEmpty, toFixed, toFixedValue } from "../../../../utils/helpers";

import CardHeader from "../../../../components/CardHeader";
import { useDeepMemo, useEntityPrivilege, useSetting } from "../../../../hooks";

import usePoView from "./usePoView";
import { Entity, InventoryStatus, QcStatus, Role } from "../../../../utils/types";
import SettingKeys from "../../../../utils/SettingKeys";
import hasPrivilege from "../../../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../../../utils/privilege";
import { UOM } from "../../../../constants";
import { colorLightGreen, colorTomatoRed, colorOrange, colorSecondary } from "../../../../utils/colors";
import WarehouseTransferDrawer from "../WarehouseTransferDrawer";

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const renderQcStatus = value => (
  <span
    style={{
      backgroundColor:
        value === QcStatus.PASS
          ? colorLightGreen.dark
          : value === QcStatus.FAIL
          ? colorTomatoRed.dark
          : value === QcStatus.ON_HOLD
          ? colorOrange.dark
          : value === QcStatus.PENDING
          ? colorSecondary.dark
          : "none",
      color: "white",
      padding: "4px 8px",
      borderRadius: 4
    }}>
    {value}
  </span>
);

const MaterialTransferPOView = props => {
  const { roles, hasAccess, history } = props;

  const { version, selectedKey, flowInfoColumns, flowInfoRows, whTxnRows, rollList, allWhList, drawerState, setDrawerState, onRowClick, onRefresh } =
    usePoView(props);

  const { disableCreate } = useEntityPrivilege("MaterialTransfer");
  const invoiceFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.INVOICES);
  const fabricItemFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FABRIC_ITEMS);
  const fabricFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FABRICS);

  const selectedRow = flowInfoRows.find(e => e.key === selectedKey);
  const parentKey = selectedRow?.parentKey;

  const whTxnColumns = useDeepMemo(() => {
    // let fields = [...invoiceFields, ...fabricFields, ...fabricItemFields, "associatedQty", "allocatedQty"];
    // if (allWhList?.length > 1) {
    //   fields.push("warehouse");
    // }
    let columns = [
      { dataKey: "txn", title: "TXN", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "externalId", title: "Ext. TXN", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "challanNo", title: "Challan No", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "ackNo", title: "Ack No", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "fromWarehouse", title: "From Warehouse" },
      { dataKey: "toWarehouse", title: "To Warehouse" },
      {
        dataKey: "invoiceNo",
        title: "Invoice No",
        render: (value, shortValue) => shortValue || "-",
        linkPath: row => history.push(`/warehouse/invoices/${row.invoiceId}/view`),
        filterOptions: { filter: true }
      },
      { dataKey: "grn", title: "GRN", render: (value, shortValue) => shortValue || "-", filterOptions: { filter: true } },
      {
        dataKey: "itemCode",
        title: "Item Code",
        render: (value, shortValue) => shortValue || "-",
        filterOptions: { filter: true },
        linkPath: row => history.push(`/warehouse/fabrics/${row.fabricId}/view`)
      },
      { dataKey: "itemDesc", title: "Item Description", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "effFabColor", title: "Fabric Color" },
      { dataKey: "colorShade", title: "Color Shade", filterOptions: { filter: true } },
      { dataKey: "fabricPo", title: "Fabric PO" },
      {
        dataKey: "orderingWidth",
        title: "Ordering Width",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uomWidth]}`),
        filterOptions: { filter: true }
      },
      {
        dataKey: "transferredQty",
        title: "Transferred Qty",
        render: (value, _, row) =>
          isEmpty(value)
            ? ""
            : `${toFixed(value)} m${row.uomLength !== UOM.METER.value ? ` (${toFixedValue(row.ledgerQty)} ${UomCodeFromValue[row.uomLength]})` : ""}`
      },
      { dataKey: "time", title: "Time" },
      { dataKey: "user", title: "User", filterOptions: { filter: true } }
    ];

    if (whTxnRows.every(row => isEmpty(row.externalId))) {
      columns = columns.filter(c => c.dataKey !== "externalId");
    }
    if (whTxnRows.every(row => isEmpty(row.ackNo))) {
      columns = columns.filter(c => c.dataKey !== "ackNo");
    }

    return columns;
  }, [invoiceFields, fabricFields, fabricItemFields, allWhList, whTxnRows]);

  const rollColumns = useDeepMemo(() => {
    let columnList = [
      { dataKey: "factoryRollNo", title: "Factory Roll No" },
      { dataKey: "supplierRollNo", title: "Supplier Roll No" },
      { dataKey: "invStatus", title: "Inv. Status", align: "center", filterOptions: { filter: true } },
      { dataKey: "qcStatus", title: "QC Status", align: "center", filterOptions: { filter: true }, render: renderQcStatus },
      { dataKey: "weight", title: "Weight", render: value => (isEmpty(value) ? "" : `${toFixedValue(value, 3)} kg`) },
      { dataKey: "length", title: "Length", render: value => (isEmpty(value) ? "" : `${toFixedValue(value)} m`) },
      { dataKey: "invoiceNo", title: "Invoice No", filterOptions: { filter: true }, render: (value, shortValue) => shortValue || "-" },
      { dataKey: "grn", title: "GRN", render: (value, shortValue) => shortValue || "-", filterOptions: { filter: true } },
      { dataKey: "itemCode", title: "Item Code", render: (value, shortValue) => shortValue || "-", filterOptions: { filter: true } },
      { dataKey: "effFabColor", title: "Fabric Color", filterOptions: { filter: true }, render: (value, shortValue) => shortValue || "-" },
      { dataKey: "colorShade", title: "Color Shade", filterOptions: { filter: true }, render: (value, shortValue) => shortValue || "-" },
      { dataKey: "fpo", title: "Fabric PO", filterOptions: { filter: true }, render: (value, shortValue) => shortValue || "-" },
      {
        dataKey: "orderingWidth",
        title: "Ordering Width",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uomWidth]}`),
        filterOptions: { filter: true }
      },
      { dataKey: "grnWarehouse", filterOptions: { filter: true }, title: "GRN \nWarehouse", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "currWarehouse", filterOptions: { filter: true }, title: "Current \nWarehouse", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "cutOrder", filterOptions: { filter: true }, title: "Cut Order", render: (value, shortValue) => shortValue || "-" }
    ];
    if (rollList.every(e => isEmpty(e.weight) || e.weight === 0.0)) {
      columnList = columnList.filter(c => c.dataKey !== "weight");
    }

    if (roles?.includes(Role.SUPER_USER) || roles?.includes(Role.MASTER)) {
      columnList.push(
        {
          dataKey: "extRemarks",
          title: "Ext Remarks"
        }
      )
    }

    return columnList;
  }, [rollList]);

  const rows = rollList.filter(e => e.invStatus !== InventoryStatus.ISSUED);

  const selectActions = useDeepMemo(() => {
    let actions = [
      {
        name: "transfer",
        tooltip: "Transfer",
        showLabel: true,
        options: { variant: "contained", color: "primary", style: { marginLeft: "1em", width: 120 } }
      }
    ];
    return actions;
  }, []);

  const onSelectActionClick = (event, action, selectedRows, onActionComplete) => {
    if (action === "transfer") {
      setDrawerState({ open: true, rollList: selectedRows });
    }
  };

  const titleSelected = selectedRows => {
    const selectedLength = selectedRows.reduce((acc, e) => acc + e.length, 0);
    const selectedWeight = selectedRows.reduce((acc, e) => acc + (e.weight || 0), 0);

    return `${selectedRows?.length} Rolls: ${toFixed(selectedLength)} m ${selectedWeight > 0 ? ` (${toFixed(selectedWeight)} kg)` : ""}  selected`;
  };

  const defaultExpanded = true;
  const hasTransferPrivilege = hasPrivilege(roles, hasAccess, ResourceId.MATERIAL_TRANSFER, Scope.WRITE) && !disableCreate;

  return (
    <div>
      <div style={{ margin: "0 1.5em", marginTop: "1em" }}>
        <Typography variant="h5">Material Transfer - By Order</Typography>
      </div>

      <WarehouseTransferDrawer
        open={drawerState?.open || false}
        ledger={drawerState?.ledger}
        rollList={drawerState?.rollList}
        roles={roles}
        hasAccess={hasAccess}
        onRefresh={onRefresh}
        onDrawerClose={() => setDrawerState({ open: false })}
      />

      <div style={{ margin: "1.5em", marginTop: 0 }}>
        {/* Flow Details */}
        <div style={{ marginTop: "1.5em" }}>
          <MuiTable
            title={<CardHeader style={{ padding: 0 }} title="Order Details" />}
            columns={flowInfoColumns}
            rows={flowInfoRows}
            toolbar
            searchable
            searchKeys={["flowRef"]}
            idKey="key"
            parentIdKey="parentKey"
            defaultExpanded={defaultExpanded}
            onRowClick={onRowClick}
            rowStyle={({ row }) => ({ backgroundColor: row?.key === selectedKey ? "#BFDDF8" : row?.key === parentKey ? "#C6D0DA" : "none" })}
            i18nKey="resources.orders.fields.flowInfo"
          />
        </div>

        {/* Warehouse Transfer Transactions */}
        <div style={{ marginTop: "1.5em" }}>
          <MuiTable
            title={<CardHeader style={{ padding: 0 }} title="Transfer History" />}
            columns={whTxnColumns}
            rows={whTxnRows}
            onRowClick={row =>
              setDrawerState({
                open: true,
                rollList: Array.isArray(rollList) ? rollList.filter(ri => row.rollList?.map(e => e.id).includes(ri.id)) : row.rollList,
                ledger: row
              })
            }
            searchable
            pageable
            searchKeys={["txn", "challanNo", "ackNo", "fabricPo"]}
            i18nKey="resources.quality.fields.invoice"
          />
        </div>

        {/** Roll Inventory */}
        <div style={{ marginTop: "1.5em" }}>
          <MuiTable
            key={version}
            title={<CardHeader style={{ padding: 0 }} title="Roll Inventory" />}
            titleSelected={titleSelected}
            columns={rollColumns}
            rows={rows}
            pageable
            pageSelect={false}
            searchable
            searchKeys={["rollNo", "supplierRollNo"]}
            selectable={hasTransferPrivilege}
            selectActions={selectActions}
            onSelectActionClick={onSelectActionClick}
            pageSize={15}
            rowsPerPageOptions={[15, 25, 100]}
            i18nKey="resources.rolls.fields"
          />
        </div>
      </div>
    </div>
  );
};

export default translate(MaterialTransferPOView);
